import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, Newspaper, LineChart, Bot } from "lucide-react";
import { api } from "@/lib/api";

const ASSET_TYPES = [
  { id: "crypto", label: "Crypto" },
  { id: "stock", label: "Stock" },
  { id: "bond", label: "Bond" },
];

function sessionId() {
  let s = localStorage.getItem("neo_ai_session");
  if (!s) {
    s = `s_${Date.now().toString(36)}`;
    localStorage.setItem("neo_ai_session", s);
  }
  return s;
}

export default function AICopilot() {
  const [sid] = useState(sessionId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const [digest, setDigest] = useState("");
  const [digestBusy, setDigestBusy] = useState(false);

  const [insAsset, setInsAsset] = useState("crypto");
  const [insSymbol, setInsSymbol] = useState("BTC");
  const [insight, setInsight] = useState("");
  const [insBusy, setInsBusy] = useState(false);

  useEffect(() => {
    api
      .get(`/ai/history/${sid}`)
      .then(({ data }) => setMessages(data))
      .catch(() => {});
  }, [sid]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sid, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI request failed");
      setMessages((m) => [...m, { role: "assistant", content: "⚠️ Sorry, I couldn't respond just now. Please try again." }]);
    } finally {
      setSending(false);
    }
  };

  const genDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.get("/ai/digest");
      setDigest(data.digest);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Digest failed");
    } finally {
      setDigestBusy(false);
    }
  };

  const genInsight = async () => {
    if (!insSymbol.trim()) {
      toast.error("Enter a symbol");
      return;
    }
    setInsBusy(true);
    setInsight("");
    try {
      const { data } = await api.post("/ai/insight", {
        asset_type: insAsset,
        symbol: insSymbol.trim(),
      });
      setInsight(data.insight);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Insight failed");
    } finally {
      setInsBusy(false);
    }
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="ai-copilot-page">
      <header className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 flex items-center gap-2">
          <Sparkles className="w-3 h-3" /> // ai · copilot
        </div>
        <h1 className="mt-2 text-4xl md:text-5xl font-black tracking-tighter uppercase">
          Neo.Copilot
        </h1>
        <p className="mt-3 text-sm text-neutral-400 max-w-2xl">
          Your AI markets assistant powered by Claude. Ask about any asset, get a position
          insight, or pull a live market digest. Educational only — not financial advice.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chat */}
        <section className="lg:col-span-2 border border-neo-border bg-neo-surface flex flex-col h-[70vh]" data-testid="ai-chat">
          <div className="px-5 py-3 border-b border-neo-border font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 flex items-center gap-2">
            <Bot className="w-3.5 h-3.5" /> assistant chat
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="ai-messages">
            {messages.length === 0 && (
              <div className="text-neutral-500 font-mono text-xs space-y-2">
                <p>Try asking:</p>
                <div className="flex flex-wrap gap-2">
                  {["What's the risk with holding BTC?", "Explain treasury yields simply", "How do I diversify a small portfolio?"].map((q) => (
                    <button
                      key={q}
                      data-testid="ai-suggestion"
                      onClick={() => setInput(q)}
                      className="border border-neo-border px-3 py-1.5 hover:bg-white/5 transition-colors text-left"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`ai-msg-${m.role}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === "user"
                      ? "bg-white text-black"
                      : "border border-neo-border text-neutral-200"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="border border-neo-border px-4 py-3 text-sm text-neutral-400 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> thinking…
                </div>
              </div>
            )}
          </div>
          <div className="border-t border-neo-border p-3 flex gap-2">
            <input
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask Neo.Copilot…"
              className="flex-1 bg-transparent border border-neo-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-white transition-colors"
            />
            <button
              data-testid="ai-send"
              onClick={send}
              disabled={sending || !input.trim()}
              className="bg-white text-black border border-white px-5 font-mono uppercase tracking-widest text-xs hover-invert disabled:opacity-40 flex items-center gap-2"
            >
              <Send className="w-3.5 h-3.5" /> Send
            </button>
          </div>
        </section>

        {/* Right rail */}
        <div className="space-y-6">
          {/* Digest */}
          <section className="border border-neo-border bg-neo-surface" data-testid="ai-digest">
            <div className="px-5 py-3 border-b border-neo-border font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 flex items-center gap-2">
              <Newspaper className="w-3.5 h-3.5" /> market digest
            </div>
            <div className="p-5">
              <button
                data-testid="ai-digest-btn"
                onClick={genDigest}
                disabled={digestBusy}
                className="w-full border border-neo-border py-2.5 font-mono uppercase tracking-widest text-[11px] hover:bg-white/5 transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {digestBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {digest ? "Refresh digest" : "Generate digest"}
              </button>
              {digest && (
                <div className="mt-4 text-xs text-neutral-300 whitespace-pre-wrap leading-relaxed" data-testid="ai-digest-text">
                  {digest}
                </div>
              )}
            </div>
          </section>

          {/* Position insight */}
          <section className="border border-neo-border bg-neo-surface" data-testid="ai-insight">
            <div className="px-5 py-3 border-b border-neo-border font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 flex items-center gap-2">
              <LineChart className="w-3.5 h-3.5" /> position insight
            </div>
            <div className="p-5 space-y-3">
              <div className="flex gap-2">
                <select
                  data-testid="ai-insight-asset"
                  value={insAsset}
                  onChange={(e) => setInsAsset(e.target.value)}
                  className="bg-neo-surface border border-neo-border px-3 py-2.5 font-mono text-xs uppercase tracking-widest focus:outline-none focus:border-white"
                >
                  {ASSET_TYPES.map((a) => (
                    <option key={a.id} value={a.id}>{a.label}</option>
                  ))}
                </select>
                <input
                  data-testid="ai-insight-symbol"
                  value={insSymbol}
                  onChange={(e) => setInsSymbol(e.target.value.toUpperCase())}
                  placeholder="Symbol (e.g. BTC)"
                  className="flex-1 bg-transparent border border-neo-border px-3 py-2.5 font-mono text-sm focus:outline-none focus:border-white transition-colors"
                />
              </div>
              <button
                data-testid="ai-insight-btn"
                onClick={genInsight}
                disabled={insBusy}
                className="w-full bg-white text-black border border-white py-2.5 font-mono uppercase tracking-widest text-[11px] hover-invert disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {insBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Get insight
              </button>
              {insight && (
                <div className="mt-2 text-xs text-neutral-300 whitespace-pre-wrap leading-relaxed" data-testid="ai-insight-text">
                  {insight}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
